/**
 * FlowAI Playbook-Editor — Markdown + Frontmatter Editor mit Versions-History.
 *
 * @page /flowai/playbooks/new
 * @page /flowai/playbooks/:id
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader, Card, Button, Badge } from '../../components/ui';
import FlowMdEditor from '../../components/FlowMdEditor';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

const PLAYBOOK_TEMPLATE = `---
name: my_playbook
trigger_type: [case_stage_changed]
confidence_threshold: 0.7
enabled: true
---

# Playbook: My Playbook

## Ziel
Kurze Beschreibung was dieses Playbook tut.

## Schritte
1. Skill X aufrufen
2. Spam-Check
3. Skill Y aufrufen
4. Pending-Action erstellen (mit Approval)
`;

export default function FlowAIPlaybookEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [body, setBody] = useState(PLAYBOOK_TEMPLATE);
  const [workspaces, setWorkspaces] = useState([]);

  async function load() {
    if (isNew) {
      setDoc({
        name: 'neues_playbook',
        workspace_id: Number(search.get('workspace_id')) || null,
        enabled: false,
        markdown_body: PLAYBOOK_TEMPLATE,
        version: 1,
      });
      setBody(PLAYBOOK_TEMPLATE);
      return;
    }
    const r = await fetch(`${API}/api/flowai/playbooks/${id}`, { credentials: 'include' });
    const raw = await r.json().catch(() => ({}));
    const d = raw?.playbook ?? raw ?? {};
    setDoc(d);
    setBody(d.playbook_md || d.markdown_body || PLAYBOOK_TEMPLATE);
  }
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    fetch(`${API}/api/flowai/workspaces`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setWorkspaces(Array.isArray(d) ? d : (d?.workspaces ?? [])))
      .catch(() => setWorkspaces([]));
  }, []);

  async function save() {
    setSaving(true);
    const payload = { ...doc, playbook_md: body };
    const url = isNew ? `${API}/api/flowai/playbooks` : `${API}/api/flowai/playbooks/${id}`;
    const method = isNew ? 'POST' : 'PUT';
    const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const raw = await r.json().catch(() => ({}));
    const out = raw?.playbook ?? raw ?? {};
    setSaving(false);
    if (r.ok && isNew && out.id) navigate(`/flowai/playbooks/${out.id}`);
    else if (r.ok) load();
    else alert('Speichern fehlgeschlagen: ' + (raw.error || r.status));
  }

  if (!doc) return <div className="p-6 text-sm">Lade…</div>;

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[
        { label: 'Playbooks', to: '/flowai/playbooks' },
        { label: isNew ? 'Neues Playbook' : doc.name },
      ]} />
      <PageHeader
        title={isNew ? 'Neues Playbook' : doc.name}
        subtitle={isNew ? 'Workspace, Trigger-Type und Markdown-Body festlegen' : `Playbook #${doc.id}${doc.version ? ` · v${doc.version}` : ''}`}
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/flowai/playbooks')}>← Liste</Button>
            {!isNew && (
              <Button variant="ghost" onClick={() => navigate(`/flowai/playbooks/${id}/builder`)}>UI-Builder →</Button>
            )}
            <Button onClick={save} disabled={saving}>{saving ? 'Speichere…' : 'Speichern'}</Button>
          </div>
        }
      />
      <Card className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <Field label="Name">
            <input value={doc.name || ''} onChange={(e) => setDoc({ ...doc, name: e.target.value })} className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm" />
          </Field>
          <Field label="Workspace">
            <select
              value={doc.workspace_id || ''}
              onChange={(e) => setDoc({ ...doc, workspace_id: Number(e.target.value) || null })}
              className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm bg-white"
            >
              <option value="">— Workspace wählen —</option>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>{w.name} (#{w.id})</option>
              ))}
            </select>
          </Field>
          <Field label="Aktiv">
            <input type="checkbox" checked={!!doc.enabled} onChange={(e) => setDoc({ ...doc, enabled: e.target.checked })} />
            <Badge variant={doc.enabled ? 'success' : 'default'} className="ml-2">{doc.enabled ? 'aktiv' : 'inaktiv'}</Badge>
          </Field>
        </div>
      </Card>

      <Card title="Markdown-Body" subtitle="Frontmatter + Drehbuch">
        <FlowMdEditor value={body} onChange={setBody} minHeight={500} />
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-1">{label}</div>
      {children}
    </label>
  );
}
